import {
  AfterpayLogo,
  CashAppAfterpayLogo,
  ClearpayLogo,
  LogoProps,
} from "client/components/Logo";

export type Branding = "Afterpay" | "Clearpay" | "CashApp";
export type BrandTitle = "Afterpay" | "Clearpay";
type BrandName = "Afterpay" | "Clearpay" | "Cash App Afterpay";

const AreaBrandMap: Record<string, Branding> = {
  Australia: "Afterpay",
  Canada: "Afterpay",
  "Europe/London": "Clearpay",
  America: "CashApp",
  Auckland: "Afterpay",
};

const CountryCodeBrandMap: Record<string, Branding> = {
  AU: "Afterpay",
  CA: "Afterpay",
  GB: "Clearpay",
  UK: "Clearpay",
  NZ: "Afterpay",
  US: "CashApp",
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export interface L10n {
  brandKey: Branding; // Used in code to determine which brand is being used
  brandName: BrandName; // Used in marketing / logo alt text for logos / document title etc
  brandTitle: BrandTitle; // Used in body copy etc
  brandUrl: string;
  Logo: React.FC<LogoProps>;
  favicon: string;
}

type BrandMap = Record<Branding, L10n>;
export const getBrandMap = (): BrandMap => ({
  Afterpay: {
    brandKey: "Afterpay",
    brandName: "Afterpay",
    brandTitle: "Afterpay",
    brandUrl: "https://www.afterpay.com",
    Logo: AfterpayLogo,
    favicon: "/favicon.ico",
  },
  Clearpay: {
    brandKey: "Clearpay",
    brandName: "Clearpay",
    brandTitle: "Clearpay",
    brandUrl: "https://www.clearpay.co.uk",
    Logo: ClearpayLogo,
    favicon: "/favicon.ico",
  },
  CashApp: {
    brandKey: "CashApp",
    brandName: "Cash App Afterpay",
    brandTitle: "Afterpay",
    brandUrl: "https://www.afterpay.com",
    Logo: CashAppAfterpayLogo,
    favicon: "/favicon-cash.ico",
  },
});

const getBrandingFromCountryParam = (): Branding | null => {
  try {
    const urlParams = new URLSearchParams(location?.search);
    const country = urlParams.get("country")?.toUpperCase();

    if (country && CountryCodeBrandMap.hasOwnProperty(country)) {
      return CountryCodeBrandMap[country];
    }
  } catch (err) {
    const message = getErrorMessage(err);
    console.error("BRANDING_DECISION_ENGINE:COUNTRY_PARAM", message);
  }

  return null;
};

const getClearpayBrandingFromUrl = (): Branding | null => {
  if (location?.href.toLowerCase().includes("clearpay")) {
    return "Clearpay";
  }

  return null;
};

const getBrandingFromTimezone = (): Branding | null => {
  const timezone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;

  if (timezone) {
    const areas = Object.keys(AreaBrandMap);
    const area = areas.find((area) =>
      timezone.toLowerCase().includes(area.toLowerCase())
    );

    if (area) {
      // Canada uses American timezone names, so we need to check the language to determine the brand
      // If the country is Canada, we should return Afterpay otherwise we should return CashApp
      if (area === "America") {
        const langCountry = getCountryCodeFromBrowserLanguage();
        if (langCountry === "CA") {
          return "Afterpay";
        }
      }

      return AreaBrandMap[area];
    }
  }

  return null;
};

const getCountryCodeFromBrowserLanguage = (): string | null => {
  try {
    // ignoring the error because the type definition for Intl.Locale is not available in this version of TypeScript
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const locale = new Intl.Locale(navigator.language);
    return locale.region ?? null;
  } catch (err) {
    const message = getErrorMessage(err);
    console.error("BRANDING_DECISION_ENGINE:BROWSER_LANG", message);
  }

  return null;
};

const getBrandingFromBrowserLanguage = (): Branding | null => {
  const countryCode = getCountryCodeFromBrowserLanguage();

  if (countryCode && CountryCodeBrandMap.hasOwnProperty(countryCode)) {
    return CountryCodeBrandMap[countryCode];
  }

  return null;
};

export const getBrandingFromDecisionEngine = (): Branding =>
  getBrandingFromCountryParam() ||
  getClearpayBrandingFromUrl() ||
  getBrandingFromTimezone() ||
  getBrandingFromBrowserLanguage() ||
  "Afterpay";
